import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { getCurrentAccount } from '../../lib/auth';
import {
  RefreshCw, Bot, Loader2, CheckCircle, XCircle, Clock, ShieldAlert, Timer,
} from 'lucide-react';

const STATUS = {
  queued: { icon: Clock, cls: 'text-slate-500', label: 'Queued' },
  running: { icon: Loader2, cls: 'text-blue-600 animate-spin', label: 'Running' },
  awaiting_2fa: { icon: ShieldAlert, cls: 'text-amber-600', label: 'Awaiting 2FA' },
  completed: { icon: CheckCircle, cls: 'text-emerald-600', label: 'Completed' },
  timeout: { icon: Timer, cls: 'text-orange-600', label: 'Timed out' },
  failed: { icon: XCircle, cls: 'text-rose-600', label: 'Failed' },
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—');

export default function MigrationBotsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sweeping, setSweeping] = useState(false);
  const acct = getCurrentAccount();

  const load = useCallback(async () => {
    if (!acct?.id) { setLoading(false); return; }
    try {
      const { data } = await api.get('/ubm-migration/scraper/monitor', { params: { account_id: acct?.id } });
      setData(data);
    } catch {
      setData({ runs: [], active_count: 0, totals: {} });
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acct?.id]);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000); // live-refresh; active bots update every ~5s
    return () => clearInterval(t);
  }, [load]);

  const runWatchdog = async () => {
    setSweeping(true);
    try {
      await api.post('/jobs/migration_scraper_watchdog/trigger');
      setTimeout(load, 1500);
    } finally { setTimeout(() => setSweeping(false), 1500); }
  };

  if (loading) return <div className="flex justify-center h-64 items-center"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;

  const t = data?.totals || {};
  return (
    <div className="space-y-6" data-testid="migration-bots-panel">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Bot className="h-6 w-6" /> Migration Bots</h1>
          <p className="text-sm text-muted-foreground">Playwright/Chromium scrape runs — watchdog keeps them from hanging (stale &amp; interrupted runs auto-timeout).</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="rounded-none" onClick={runWatchdog} disabled={sweeping} data-testid="migration-bots-watchdog-btn">
            {sweeping ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ShieldAlert className="h-4 w-4 mr-1" />} Run Watchdog
          </Button>
          <Button variant="ghost" size="icon" onClick={load} data-testid="migration-bots-refresh"><RefreshCw className="h-4 w-4" /></Button>
        </div>
      </div>

      <div className="grid grid-cols-3 md:grid-cols-6 gap-2" data-testid="migration-bots-totals">
        {['running', 'queued', 'awaiting_2fa', 'completed', 'timeout', 'failed'].map((k) => (
          <div key={k} className="border rounded-none p-3 text-center">
            <div className="text-2xl font-bold">{t[k] || 0}</div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{k.replace('_', ' ')}</div>
          </div>
        ))}
      </div>

      <div className="border rounded-none divide-y">
        <div className="grid grid-cols-12 gap-2 px-4 py-2 text-xs font-medium text-muted-foreground bg-muted/30">
          <div className="col-span-2">Kind</div><div className="col-span-2">Status</div>
          <div className="col-span-4">Progress</div><div className="col-span-2">Started</div><div className="col-span-2">Heartbeat</div>
        </div>
        {(data?.runs || []).length === 0 ? (
          <div className="text-center p-8 text-muted-foreground text-sm" data-testid="migration-bots-empty">No scrape runs yet</div>
        ) : (
          data.runs.map((r) => {
            const s = STATUS[r.status] || STATUS.queued;
            const Icon = s.icon;
            return (
              <div key={r.id} className="grid grid-cols-12 gap-2 px-4 py-2.5 text-sm items-center" data-testid={`migration-bot-run-${r.id}`}>
                <div className="col-span-2 font-medium">{(r.kind || '').replace('_', ' ')}</div>
                <div className="col-span-2"><Badge variant="outline" className={`rounded-none gap-1 ${s.cls}`}><Icon className="h-3 w-3" /> {s.label}</Badge></div>
                <div className="col-span-4 text-xs text-muted-foreground truncate" title={r.progress?.message}>{r.progress?.message || '—'}</div>
                <div className="col-span-2 text-xs text-muted-foreground">{fmt(r.started_at)}</div>
                <div className="col-span-2 text-xs text-muted-foreground">{fmt(r.last_heartbeat)}</div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
